import { Kind } from "graphql";
import { simpleHash } from "../cachingUtils/simpleHash";
import { isDefinedOnQuery } from "../graphql/documentUtils/composableFragment";
import { findEntryPointDefinition } from "../graphql/documentUtils/entrypoint";
const hashCache = new Map();
// the hashes that we calculate with this helper serve a few purposes:
// - Enables the fetching pipeline to identify fragments and queries with a hash. A fragment and its
//   associated query always obtain the same hash result. This assures that the hashes we calculate
//   for each top level field in useRootQuery can then be reproducible in useFragment call.
// - Top level fields and variables are prefixed by a chain of hashes. This prevents collisions
//   between variables inside a composition fragment thanks to hashes being deterministic.
//
// The documents that it can calculate the hash for are fragmets defined `on Query` and Query
// operations themselves.
//
export function generateHashForDefinitionNode(document, definitionNode) {
    const fieldsToHash = findTopLevelFieldSelectionsToHash(definitionNode, document.definitions);
    return simpleHash(fieldsToHash);
}
export function generateHashForDocument(document) {
    const existingHash = hashCache.get(document);
    if (existingHash) {
        return existingHash;
    }
    const definitionNode = findEntryPointDefinition(document);
    const hash = generateHashForDefinitionNode(document, definitionNode);
    hashCache.set(document, hash);
    return hash;
}
function findTopLevelFieldSelectionsToHash(fragment, allDefinitions) {
    const { selections } = fragment.selectionSet;
    const selectionsToTakeIntoAccount = selections.filter((selection)=>{
        // Ignore spreaded fragments if they are defined on Query
        if (selection.kind === 'FragmentSpread') {
            const fragmentDefinition = getFragmentByName(selection.name.value, allDefinitions);
            if (isDefinedOnQuery(fragmentDefinition)) {
                return false;
            }
            return true;
        }
        return true;
    });
    return selectionsToTakeIntoAccount.map((selection)=>serializeSelection(selection, allDefinitions)).filter((serialized)=>serialized !== '') // Filter out empty strings from ignored __typename fields
    .sort().join(',');
}
function getFragmentByName(name, allDefinitions) {
    const matchedDefinition = allDefinitions.find((def)=>{
        var _def_name;
        return 'name' in def && ((_def_name = def.name) === null || _def_name === void 0 ? void 0 : _def_name.value) === name;
    });
    if (!matchedDefinition) {
        throw new Error(`Fragment ${name} not found`);
    }
    if (matchedDefinition.kind !== Kind.FRAGMENT_DEFINITION) {
        throw new Error(`Fragment ${name} not found`);
    }
    return matchedDefinition;
}
function serializeSelection(selection, allDefinitions) {
    if (selection.kind === 'Field') {
        const fieldName = selection.name.value;
        // Ignore __typename fields for hashing purposes
        if (fieldName === '__typename') {
            return '';
        }
        const fieldData = selection.selectionSet ? `:${serializeSelectionSet(selection.selectionSet, allDefinitions)}` : '';
        const argumentsData = selection.arguments ? `(${serializeFieldArguments(selection.arguments)})` : '';
        return `${fieldName}${fieldData}${argumentsData}`;
    }
    if (selection.kind === 'FragmentSpread') {
        const spreadFragment = getFragmentByName(selection.name.value, allDefinitions);
        return serializeSelectionSet(spreadFragment.selectionSet, allDefinitions);
    }
    return '';
}
function serializeSelectionSet(selectionSet, allDefinitions) {
    return selectionSet.selections.map((selection)=>serializeSelection(selection, allDefinitions)).filter((serialized)=>serialized !== '') // Filter out empty strings from ignored __typename fields
    .sort().join(',');
}
function serializeFieldArguments(fieldArguments) {
    return fieldArguments.map((argument)=>argument.name.value).sort().join(',');
}

//# sourceMappingURL=documentHashing.js.map